import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import { useId } from '../hooks/useId'
import { RadioContext } from './RadioContext'
import type {
  RadioInputColor,
  RadioInputSize,
  StateDefiniton,
} from './RadioContext'

interface RadioItemsProps {
  /**
   * The color scheme of the radio inputs
   */
  color?: RadioInputColor
  /**
   * The initial selected value
   * @default ''
   */
  defaultValue?: string | number
  /**
   * If true, the radio group is disabled
   */
  disabled?: boolean
  /**
   * Whether the radio group is in an error state
   * @default false
   */
  error?: boolean
  /**
   * Additional descriptive text or error text to display below the radio group
   * @default ''
   */
  helperText?: string
  /**
   * The name attribute for the radio group
   */
  name?: string
  /**
   * Callback fired when the selected value changes
   */
  onChange?: (event: {
    target: { name: string; value: string | number }
  }) => void
  /**
   * The size of the radio inputs
   */
  size?: RadioInputSize
}

type ItemsContextState = Omit<StateDefiniton, 'direction'>

function Items({
  children,
  color,
  disabled,
  defaultValue = '',
  error = false,
  helperText = '',
  name,
  onChange,
  size,
}: PropsWithChildren<RadioItemsProps>): JSX.Element {
  const id = useId()
  const [state, setState]: [
    ItemsContextState,
    Dispatch<SetStateAction<ItemsContextState>>,
  ] = useState({
    color,
    disabled,
    name: name || `coconut-radio-group-${id}`,
    size,
    value: defaultValue,
  })

  useEffect(() => {
    if (disabled !== state.disabled) {
      setState((prevState) => ({ ...prevState, disabled }))
    }
  }, [disabled, state.disabled])

  const setValue = useCallback(
    (value: string | number) => {
      setState((prevState) => ({ ...prevState, value }))

      if (onChange) {
        onChange({ target: { name: state.name, value: value } })
      }
    },
    [onChange, state.name],
  )

  const setLabelledBy = useCallback(
    (id: string) => setState((prevState) => ({ ...prevState, labelledBy: id })),
    [],
  )

  return (
    <RadioContext.Provider value={[state, setValue, setLabelledBy]}>
      <>
        {children}
        {helperText ? (
          <span
            aria-live={error ? 'assertive' : 'off'}
            className={clsx(
              'text-xs',
              error ? 'text-error-main' : 'text-black-60',
            )}
          >
            {helperText}
          </span>
        ) : null}
      </>
    </RadioContext.Provider>
  )
}

export default Items
